import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { getLinks, attivaLink, getQRCode, getAziende } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, QrCode, ExternalLink, Download, CheckCircle, Building2 } from 'lucide-react';

const QRCodePage = () => {
  const { isAdmin } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [aziendaFilter, setAziendaFilter] = useState('all');
  const [aziende, setAziende] = useState([]);

  useEffect(() => {
    loadLinks();
    if (isAdmin()) {
      getAziende()
        .then((r) => setAziende(r.data || []))
        .catch(() => {});
    }
  }, []);

  const loadLinks = async () => {
    try {
      const response = await getLinks();
      setLinks(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento QR');
    } finally {
      setLoading(false);
    }
  };

  const handleAttiva = async (id) => {
    try {
      await attivaLink(id);
      toast.success('QR Code attivato');
      loadLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore attivazione');
    }
  };

  const activeLinks = links.filter(l => l.attivo && l.id_elogio);
  const inactiveLinks = links.filter(l => !l.attivo || !l.id_elogio);

  const filterLinks = (linkList) => linkList.filter(l => {
    const searchLower = search.toLowerCase();
    const matchesSearch = !search ||
      l.id?.toString().includes(search) ||
      l.id_azienda?.toLowerCase().includes(searchLower) ||
      l.id_elogio?.toLowerCase().includes(searchLower) ||
      l.nome_defunto?.toLowerCase().includes(searchLower) ||
      l.nome_azienda?.toLowerCase().includes(searchLower);
    
    let matchesAzienda = true;
    if (aziendaFilter !== 'all') {
      if (aziendaFilter === '__none__') {
        matchesAzienda = !l.id_azienda;
      } else {
        matchesAzienda = l.id_azienda === aziendaFilter;
      }
    }
    
    return matchesSearch && matchesAzienda;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const renderTable = (linkList, showActivate = false) => (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="qr-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-4 font-medium text-slate-600">QR #</th>
                <th className="text-left p-4 font-medium text-slate-600">Nome Persona</th>
                {isAdmin() && (
                  <th className="text-left p-4 font-medium text-slate-600">Agenzia</th>
                )}
                <th className="text-left p-4 font-medium text-slate-600">ID Elogio</th>
                <th className="text-center p-4 font-medium text-slate-600">Stato</th>
                <th className="text-right p-4 font-medium text-slate-600">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {linkList.map((link) => (
                <tr 
                  key={link.id} 
                  className="border-b hover:bg-slate-50 transition-colors"
                  data-testid={`qr-row-${link.id}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-slate-100">
                        <QrCode className="h-4 w-4 text-slate-600" />
                      </div>
                      <Badge variant="outline" className="font-mono">#{link.id}</Badge>
                    </div>
                  </td>
                  <td className="p-4">
                    {link.nome_defunto ? (
                      <span className="font-medium text-slate-900">{link.nome_defunto}</span>
                    ) : (
                      <span className="text-slate-400 italic">Non assegnato</span>
                    )}
                  </td>
                  {isAdmin() && (
                    <td className="p-4" data-testid={`qr-azienda-${link.id}`}>
                      {link.nome_azienda ? (
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          <span className="truncate max-w-[180px]" title={link.nome_azienda}>
                            {link.nome_azienda}
                          </span>
                        </div>
                      ) : link.id_azienda ? (
                        <span className="text-xs font-mono text-slate-500">{link.id_azienda}</span>
                      ) : (
                        <span className="text-xs text-slate-400 italic">—</span>
                      )}
                    </td>
                  )}
                  <td className="p-4">
                    {link.id_elogio ? (
                      <span className="font-mono text-xs text-slate-500">{link.id_elogio}</span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="p-4 text-center">
                    {link.attivo ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Attivo
                      </Badge>
                    ) : link.id_elogio ? (
                      <Badge className="bg-amber-100 text-amber-700">Assegnato</Badge>
                    ) : (
                      <Badge variant="secondary">Disponibile</Badge>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* Attiva */}
                      {showActivate && link.id_elogio && !link.attivo && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleAttiva(link.id)}
                          data-testid={`attiva-qr-${link.id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Attiva
                        </Button>
                      )}

                      {/* Download PNG */}
                      <a href={getQRCode(link.id)} download={`qr_${link.id}.png`}>
                        <Button variant="outline" size="sm" data-testid={`download-qr-${link.id}`}>
                          <Download className="h-4 w-4 mr-1" />
                          PNG
                        </Button>
                      </a>

                      {/* Link */}
                      {link.attivo && (
                        <a href={link.link} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {linkList.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            Nessun QR Code trovato
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="qrcode-page">
      {/* Header */}
      <div className="mb-8">
        <h1 
          className="text-4xl font-semibold text-slate-900 tracking-tight"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          QR Code
        </h1>
        <p className="text-slate-500 mt-1">Gestione link e codici QR</p>
      </div>

      {/* Search & Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Cerca per QR #, agenzia, elogio o nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-qr"
          />
        </div>
        {isAdmin() && (
          <div className="w-full sm:w-72">
            <Select value={aziendaFilter} onValueChange={setAziendaFilter}>
              <SelectTrigger data-testid="filter-azienda-qr-select">
                <SelectValue placeholder="Filtra per agenzia" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le agenzie</SelectItem>
                <SelectItem value="__none__">Senza agenzia</SelectItem>
                {aziende.map((a) => (
                  <SelectItem key={a.id_azienda} value={a.id_azienda}>
                    {a.ragione_sociale}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {isAdmin() && aziendaFilter !== 'all' && (
          <Button 
            variant="ghost"
            onClick={() => setAziendaFilter('all')}
            className="text-slate-600"
            data-testid="clear-filter-qr-btn"
          >
            Reset filtro
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="attivi" className="space-y-4">
        <TabsList>
          <TabsTrigger value="attivi" data-testid="tab-attivi">
            QR Attivi ({activeLinks.length})
          </TabsTrigger>
          <TabsTrigger value="disponibili" data-testid="tab-disponibili">
            Non Attivi / Disponibili ({inactiveLinks.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="attivi">
          {renderTable(filterLinks(activeLinks))}
        </TabsContent>

        <TabsContent value="disponibili">
          {renderTable(filterLinks(inactiveLinks), true)}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QRCodePage;
